import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  TextField,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Link as LinkIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  Home as HomeIcon,
  Apartment as ApartmentIcon,
  ContentCopy as ContentCopyIcon,
  Webhook as WebhookIcon,
  Settings as SettingsIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useLoader } from '../../contexts/LoaderContext';
import { useModal } from '../../contexts/ModalContext';
import MagicBricksLogo from '../../assets/images/magicbricks.png';
import NinetyNineAcresLogo from '../../assets/images/99acreslogo.png';
import HousingLogo from '../../assets/images/Housing.com_Logo.png';
import GoogleAdsLogo from '../../assets/images/Google-AdWords-logo.png';

const getIntegrationIcon = (integrationId) => {
  switch (integrationId) {
    case 'facebook':
      return <FacebookIcon fontSize="large" />;
    case 'whatsapp':
      return <WhatsAppIcon fontSize="large" />;
    case 'magicbricks':
      return (
        <img 
          src={MagicBricksLogo} 
          alt="MagicBricks Logo"
          style={{ width: '32px', height: '32px', objectFit: 'contain' }}
        />
      );
    case '99acres':
      return (
        <img 
          src={NinetyNineAcresLogo} 
          alt="99acres Logo"
          style={{ width: '32px', height: '32px', objectFit: 'contain' }}
        />
      );
    case 'housing':
      return (
        <img 
          src={HousingLogo} 
          alt="Housing.com Logo"
          style={{ width: '32px', height: '32px', objectFit: 'contain' }}
        />
      );
    case 'google_ads':
      return (
        <img 
          src={GoogleAdsLogo} 
          alt="Google Ads Logo"
          style={{ width: '32px', height: '32px', objectFit: 'contain' }}
        />
      );
    default:
      return <LinkIcon fontSize="large" />;
  }
};

const Integrations = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showLoader, hideLoader } = useLoader();
  const { openSuccessModal, openErrorModal } = useModal();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disconnectDialog, setDisconnectDialog] = useState({ open: false, integration: null });
  const [configDialog, setConfigDialog] = useState({ open: false, integration: null, apiKey: '', webhookUrl: '' });
  const [pageSelectionDialog, setPageSelectionDialog] = useState({ open: false, pages: [], loading: false });
  const [connectedPages, setConnectedPages] = useState([]);
  const [isFetchingIntegrations, setIsFetchingIntegrations] = useState(false);
  const [customWebhooksCount, setCustomWebhooksCount] = useState(0);
  const [whatsappWizard, setWhatsappWizard] = useState({ open: false, step: 1, wabas: [], phoneNumbers: [], selectedWaba: null, selectedPhone: null });
  const [whatsappWizardLoading, setWhatsappWizardLoading] = useState(false);

  useEffect(() => {
    console.log('Integrations component mounted, checking URL params...');
    fetchIntegrations();
    fetchCustomWebhooksCount();
    
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');
    const whatsappOAuth = urlParams.get('whatsapp_oauth');
    
    if (success === 'true') {
      setSearchParams({}, { replace: true });
      hideLoader();
      openSuccessModal('Facebook Page connected successfully!');
      fetchIntegrations();
    } else if (error) {
      setSearchParams({}, { replace: true });
      hideLoader();
      openErrorModal('Failed to connect Facebook Page');
    } else if (whatsappOAuth === 'success') {
      const accountId = urlParams.get('account_id');
      console.log('✓ WhatsApp OAuth success detected from URL params!', 'Account ID:', accountId);
      window.history.replaceState({}, document.title, window.location.pathname);
      hideLoader();
      setWhatsappWizard({ open: true, step: 1, wabas: [], phoneNumbers: [], selectedWaba: null, selectedPhone: null });
      setWhatsappWizardLoading(false);
      setTimeout(() => fetchWABAs(), 100);
    }
    
    // Listen for OAuth callback messages
    const handleMessage = (event) => {
      if (!event.data || !event.data.type) return;
      
      const frontendOrigin = window.location.origin;
      const apiBaseUrl = process.env.REACT_APP_SERVER_URL || '/api';
      let backendOrigin = null;
      if (apiBaseUrl.startsWith('http://') || apiBaseUrl.startsWith('https://')) {
        try {
          const url = new URL(apiBaseUrl);
          backendOrigin = url.origin;
        } catch (e) {}
      }
      
      const isValidOrigin = event.origin === frontendOrigin ||
                           (backendOrigin && event.origin === backendOrigin) ||
                           event.origin.includes('ngrok') ||
                           event.origin.includes('localhost') ||
                           event.origin.includes('127.0.0.1') ||
                           event.data?.type === 'WHATSAPP_OAUTH_SUCCESS' ||
                           (process.env.NODE_ENV === 'development');
      
      if (!isValidOrigin) {
        console.warn('Rejected message from origin:', event.origin, 'Expected:', frontendOrigin);
        return;
      }
      
      if (event.data.type === 'FACEBOOK_OAUTH_SUCCESS') {
        hideLoader();
        fetchFacebookPages();
      } else if (event.data.type === 'FACEBOOK_CONNECT_SUCCESS') {
        hideLoader();
        openSuccessModal(event.data.message || 'Facebook Page connected successfully!');
        fetchIntegrations(false);
      } else if (event.data.type === 'FACEBOOK_CONNECT_ERROR') {
        hideLoader();
        openErrorModal(event.data.message || 'Failed to connect Facebook Page');
      } else if (event.data.type === 'WHATSAPP_CONNECT_SUCCESS') {
        hideLoader();
        openSuccessModal(event.data.message || 'WhatsApp connected successfully!');
        fetchIntegrations(false);
      } else if (event.data.type === 'WHATSAPP_OAUTH_SUCCESS') {
        console.log('WhatsApp OAuth success received:', event.data);
        hideLoader();
        setWhatsappWizard({ open: true, step: 1, wabas: [], phoneNumbers: [], selectedWaba: null, selectedPhone: null });
        setWhatsappWizardLoading(false);
        setTimeout(() => fetchWABAs(), 100);
      } else if (event.data.type === 'WHATSAPP_CONNECT_ERROR') {
        console.error('WhatsApp connect error:', event.data);
        hideLoader();
        openErrorModal(event.data.message || 'Failed to connect WhatsApp');
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [hideLoader, openSuccessModal, openErrorModal]);

  const fetchIntegrations = async (showLoading = true) => {
    // Prevent multiple simultaneous fetches
    if (isFetchingIntegrations) {
      return;
    }
    
    try {
      setIsFetchingIntegrations(true);
      if (showLoading) {
        setLoading(true);
      }
      const response = await api.get('/lms/integrations/');
      const data = Array.isArray(response.data) ? response.data : [];
      setIntegrations(
        data.map((integration) => ({
          ...integration,
          icon: integration.icon || getIntegrationIcon(integration.id),
        }))
      );
    } catch (error) {
      console.error('Error fetching integrations:', error);
      // If endpoint doesn't exist yet, use default structure
      setIntegrations([
        {
          id: 'facebook',
          name: 'Facebook',
          description: 'Connect your Facebook Page to receive Meta Lead Ads directly in Salesgro',
          icon: getIntegrationIcon('facebook'),
          connected: false,
          connectedPage: null,
          status: 'available',
        },
        {
          id: 'magicbricks',
          name: 'MagicBricks',
          description: 'Connect your MagicBricks account to automatically receive property leads via webhook',
          icon: getIntegrationIcon('magicbricks'),
          connected: false,
          status: 'available',
        },
        {
          id: '99acres',
          name: '99acres',
          description: 'Connect your 99acres account to automatically receive property leads via webhook',
          icon: getIntegrationIcon('99acres'),
          connected: false,
          status: 'available',
        },
        {
          id: 'housing',
          name: 'Housing.com',
          description: 'Connect your Housing.com account to automatically receive property leads via webhook',
          icon: getIntegrationIcon('housing'),
          connected: false,
          status: 'available',
        },
        {
          id: 'whatsapp',
          name: 'WhatsApp',
          description: 'Connect your WhatsApp account to send and receive messages directly from Salesgro',
          icon: getIntegrationIcon('whatsapp'),
          connected: false,
          connectedPage: null,
          status: 'available',
        },
      ]);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
      setIsFetchingIntegrations(false);
    }
  };

  const fetchCustomWebhooksCount = async () => {
    try {
      const response = await api.get('/lms/integrations/custom-webhooks/');
      setCustomWebhooksCount(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching custom webhooks count:', error);
    }
  };

  const handleConnectFacebook = async () => {
    try {
      showLoader('Preparing Facebook connection...');
      
      // Get OAuth authorization URL from backend
      const response = await api.get('/lms/integrations/facebook/oauth/start/');
      const { authorization_url } = response.data;
      
      if (!authorization_url) {
        throw new Error('No authorization URL received');
      }
      
      // Open OAuth popup
      const width = 600;
      const height = 700;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      
      const popup = window.open(
        authorization_url,
        'Facebook OAuth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
      );
      
      // Check if popup was blocked
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        hideLoader();
        openErrorModal('Popup was blocked. Please allow popups for this site and try again.');
        return;
      }
      
      // Poll to check if popup is closed (user completed or cancelled)
      let messageReceived = false;
      
      const messageHandler = (event) => {
        if (event.data?.type === 'FACEBOOK_OAUTH_SUCCESS') {
          messageReceived = true;
        }
      };
      
      window.addEventListener('message', messageHandler);
      
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageHandler);
          
          // Only fetch pages if we didn't receive a message
          // (message handler already calls fetchFacebookPages)
          if (!messageReceived) {
            hideLoader();
            // Wait a bit for any late messages, then fetch pages
            setTimeout(() => {
              if (!messageReceived) {
                fetchFacebookPages();
              }
            }, 300);
          }
        }
      }, 300);
      
    } catch (error) {
      hideLoader();
      openErrorModal(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to start Facebook connection. Please try again.'
      );
    }
  };

  const handleConnectWhatsApp = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      showLoader('Preparing WhatsApp connection...');
      
      const response = await api.get('/lms/integrations/whatsapp/oauth/start/');
      const { authorization_url } = response.data;
      
      if (!authorization_url) {
        console.error('No authorization_url in response:', response.data);
        throw new Error('No authorization URL received from server');
      }
      if (!authorization_url.startsWith('https://www.facebook.com') && !authorization_url.startsWith('https://facebook.com')) {
        console.error('Invalid authorization URL:', authorization_url);
        throw new Error('Invalid authorization URL received');
      }
      
      const width = 600;
      const height = 700;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      
      console.log('Opening WhatsApp OAuth popup with URL:', authorization_url);
      const popup = window.open(
        authorization_url,
        'WhatsApp OAuth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
      );
      
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        hideLoader();
        openErrorModal('Popup was blocked. Please allow popups for this site and try again.');
        return;
      }
      
      console.log('Popup opened successfully, window.opener should be available in popup');
      window.whatsappOAuthPopup = popup;
      
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          console.log('Popup closed');
          clearInterval(checkClosed);
          hideLoader();
          setTimeout(() => {
            console.log('Popup closed, attempting to fetch WABAs...');
            setWhatsappWizard({
              open: true,
              step: 1,
              wabas: [],
              phoneNumbers: [],
              selectedWaba: null,
              selectedPhone: null,
            });
            setTimeout(() => fetchWABAs(), 200);
          }, 500);
        }
      }, 500);
      
    } catch (error) {
      console.error('Error in handleConnectWhatsApp:', error);
      hideLoader();
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to start WhatsApp connection. Please try again.';
      openErrorModal(errorMessage);
    }
  };

  const fetchWABAs = async () => {
    try {
      setWhatsappWizardLoading(true);
      const response = await api.get('/lms/integrations/whatsapp/wabas/');
      
      if (response.data.wabas && response.data.wabas.length > 0) {
        setWhatsappWizard(prev => ({
          ...prev,
          wabas: response.data.wabas || [],
          step: 2,
        }));
      } else {
        openErrorModal('No WhatsApp Business Accounts found. Please ensure you have a WABA set up in Meta Business Manager.');
        setWhatsappWizard(prev => ({ ...prev, open: false }));
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          'Failed to fetch WhatsApp Business Accounts. Please try again.';
      openErrorModal(errorMessage);
      setWhatsappWizard(prev => ({ ...prev, open: false }));
      console.error('Error fetching WABAs:', error);
    } finally {
      setWhatsappWizardLoading(false);
    }
  };

  const fetchPhoneNumbers = async (wabaId) => {
    try {
      setWhatsappWizardLoading(true);
      const response = await api.get(`/lms/integrations/whatsapp/wabas/${wabaId}/phone-numbers/`);
      setWhatsappWizard(prev => ({
        ...prev,
        phoneNumbers: response.data.phone_numbers || [],
        selectedWaba: prev.wabas.find(w => w.id === wabaId),
        step: 3,
      }));
    } catch (error) {
      openErrorModal(
        error.response?.data?.error ||
        'Failed to fetch phone numbers. Please try again.'
      );
    } finally {
      setWhatsappWizardLoading(false);
    }
  };

  const handleSaveWhatsApp = async () => {
    try {
      setWhatsappWizardLoading(true);
      const response = await api.post('/lms/integrations/whatsapp/connect/', {
        waba_id: whatsappWizard.selectedWaba.id,
        phone_number_id: whatsappWizard.selectedPhone.id,
      });
      
      openSuccessModal('WhatsApp connected successfully!');
      setWhatsappWizard({ open: false, step: 1, wabas: [], phoneNumbers: [], selectedWaba: null, selectedPhone: null });
      fetchIntegrations();
    } catch (error) {
      openErrorModal(
        error.response?.data?.error ||
        'Failed to connect WhatsApp. Please try again.'
      );
    } finally {
      setWhatsappWizardLoading(false);
    }
  };


  const handleDisconnect = async () => {
    const { integration } = disconnectDialog;
    if (!integration) return;

    try {
      showLoader('Disconnecting...');
      await api.post(`/lms/integrations/${integration.id}/disconnect/`);
      
      setDisconnectDialog({ open: false, integration: null });
      hideLoader();
      
      openSuccessModal(`${integration.name} disconnected successfully`);
      
      // Refresh integrations in background
      await fetchIntegrations(false);
    } catch (error) {
      hideLoader();
      openErrorModal(
        error.response?.data?.error || 
        error.response?.data?.message || 
        'Failed to disconnect. Please try again.'
      );
    }
  };

  const handleConfigureMagicBricks = async () => {
    try {
      showLoader('Generating webhook URL...');
      
      const response = await api.post('/lms/integrations/magicbricks/setup/');
      
      hideLoader();
      
      // Show webhook URL in a copyable format
      setConfigDialog({ 
        ...configDialog, 
        webhookUrl: response.data.webhook_url
      });
      
      // Refresh integrations in background
      fetchIntegrations(false);
      
    } catch (error) {
      hideLoader();
      openErrorModal(error.response?.data?.error || 'Failed to configure MagicBricks');
      setConfigDialog({ open: false, integration: null, apiKey: '', webhookUrl: '' });
    }
  };

  const handleConfigure99Acres = async () => {
    try {
      showLoader('Generating webhook URL...');
      
      const response = await api.post('/lms/integrations/99acres/setup/');
      
      hideLoader();
      
      // Show webhook URL in a copyable format
      setConfigDialog({ 
        ...configDialog, 
        webhookUrl: response.data.webhook_url
      });
      
      // Refresh integrations in background
      fetchIntegrations(false);
      
    } catch (error) {
      hideLoader();
      openErrorModal(error.response?.data?.error || 'Failed to configure 99acres');
      setConfigDialog({ open: false, integration: null, apiKey: '', webhookUrl: '' });
    }
  };

  const handleConfigureHousing = async () => {
    try {
      showLoader('Generating webhook URL...');
      
      const response = await api.post('/lms/integrations/housing/setup/');
      
      hideLoader();
      
      // Show webhook URL in a copyable format
      setConfigDialog({ 
        ...configDialog, 
        webhookUrl: response.data.webhook_url
      });
      
      // Refresh integrations in background
      fetchIntegrations(false);
      
    } catch (error) {
      hideLoader();
      openErrorModal(error.response?.data?.error || 'Failed to configure Housing.com');
      setConfigDialog({ open: false, integration: null, apiKey: '', webhookUrl: '' });
    }
  };

  const fetchFacebookPages = async () => {
    try {
      setPageSelectionDialog({ open: true, pages: [], loading: true });
      
      const response = await api.get('/lms/integrations/facebook/pages/');
      const { pages } = response.data;
      
      if (pages && pages.length > 0) {
        setPageSelectionDialog({ open: true, pages, loading: false });
      } else {
        // No pages found, close dialog and show error
        setPageSelectionDialog({ open: false, pages: [], loading: false });
        openErrorModal('No Facebook pages found. Please ensure you have Facebook pages and have granted the necessary permissions.');
      }
    } catch (error) {
      console.error('Error fetching Facebook pages:', error);
      // If error is 404, it means OAuth hasn't been completed yet
      if (error.response?.status === 404) {
        setPageSelectionDialog({ open: false, pages: [], loading: false });
        // Don't show error - user just needs to complete OAuth first
      } else {
        openErrorModal(
          error.response?.data?.error || 
          'Failed to fetch Facebook pages. Please try connecting again.'
        );
        setPageSelectionDialog({ open: false, pages: [], loading: false });
      }
    }
  };

  const handleConnectPage = async (pageId) => {
    try {
      showLoader('Connecting Facebook page...');
      
      const response = await api.post('/lms/integrations/facebook/pages/connect/', {
        page_id: pageId
      });
      
      // Optimistically update the page selection dialog
      setPageSelectionDialog(prev => ({
        ...prev,
        pages: prev.pages.map(page => 
          page.id === pageId ? { ...page, is_connected: true } : page
        )
      }));
      
      hideLoader();
      
      // Check if there's a warning about webhook subscription
      if (response.data.warning) {
        openErrorModal(response.data.warning);
      } else {
        openSuccessModal('Facebook page connected successfully!');
      }
      
      // Refresh integrations in background without showing loader
      await fetchIntegrations(false);
      
    } catch (error) {
      // Revert optimistic update on error
      setPageSelectionDialog(prev => ({
        ...prev,
        pages: prev.pages.map(page => 
          page.id === pageId ? { ...page, is_connected: false } : page
        )
      }));
      
      hideLoader();
      openErrorModal(
        error.response?.data?.error || 
        'Failed to connect page. Please try again.'
      );
    }
  };

  const handleDisconnectPage = async (pageId) => {
    try {
      showLoader('Disconnecting Facebook page...');
      
      await api.post('/lms/integrations/facebook/pages/disconnect/', {
        page_id: pageId
      });
      
      // Optimistically update the page selection dialog
      setPageSelectionDialog(prev => ({
        ...prev,
        pages: prev.pages.map(page => 
          page.id === pageId ? { ...page, is_connected: false } : page
        )
      }));
      
      hideLoader();
      openSuccessModal('Facebook page disconnected successfully!');
      
      // Refresh integrations in background without showing loader
      await fetchIntegrations(false);
      
    } catch (error) {
      // Revert optimistic update on error
      setPageSelectionDialog(prev => ({
        ...prev,
        pages: prev.pages.map(page => 
          page.id === pageId ? { ...page, is_connected: true } : page
        )
      }));
      
      hideLoader();
      openErrorModal(
        error.response?.data?.error || 
        'Failed to disconnect page. Please try again.'
      );
    }
  };

  const handleManagePages = async () => {
    await fetchFacebookPages();
  };

  const handleConfigureGoogleAds = async () => {
    try {
      showLoader('Generating webhook URL...');
      
      const response = await api.post('/lms/integrations/google-ads/setup/');
      
      hideLoader();
      
      // Show webhook URL in a copyable format
      setConfigDialog({ 
        ...configDialog, 
        webhookUrl: response.data.webhook_url
      });
      
      // Refresh integrations in background
      fetchIntegrations(false);
      
    } catch (error) {
      hideLoader();
      openErrorModal(error.response?.data?.error || 'Failed to configure Google Ads');
      setConfigDialog({ open: false, integration: null, apiKey: '', webhookUrl: '' });
    }
  };

  const handleCopyWebhookUrl = () => {
    if (configDialog.webhookUrl) {
      navigator.clipboard.writeText(configDialog.webhookUrl);
      openSuccessModal('Webhook URL copied to clipboard!');
    }
  };

  const generateSampleCurl = (webhookUrl, integrationType) => {
    if (!webhookUrl) return '';
    
    // Sample JSON payload based on integration type
    let samplePayload = {};
    if (integrationType === 'magicbricks') {
      samplePayload = {
        source: "Magic Bricks",
        mobile: "9791155999",
        email: "ashokraj.ok@gmail.com",
        time: "145000",
        isd: "Plaza Bounty Acres",
        name: "Ashok Raj",
        msg: "Plaza Bounty Acres, Chennai - I am interested in your project. Please contact me. Additional Details - Multistorey Apartment",
        project: "Plaza Bounty Acres",
        dt: "20180722",
        City: "Chennai"
      };
    } else if (integrationType === '99acres') {
      samplePayload = {
        name: "Test User",
        email: "test@example.com",
        mobile: "9999999999",
        property_type: "Apartment",
        location: "Mumbai",
        requirement: "Looking for 2BHK apartment"
      };
    } else if (integrationType === 'housing') {
      samplePayload = {
        name: "Test User",
        email: "test@example.com",
        mobile: "9999999999",
        property_type: "Apartment",
        location: "Mumbai",
        requirement: "Looking for 2BHK apartment"
      };
    } else if (integrationType === 'google_ads') {
      samplePayload = {
        google_key: "CAESEBhHOt4RGvXXXXXXXXXXXXX",
        is_test: false,
        user_column_data: [
          { column_id: "FULL_NAME", string_value: "Rajesh Kumar" },
          { column_id: "EMAIL", string_value: "rajesh.kumar@example.com" },
          { column_id: "PHONE_NUMBER", string_value: "+919876543210" },
          { column_id: "CITY", string_value: "Mumbai" },
          { column_id: "CUSTOM_QUESTION_1", string_value: "2BHK Apartment" }
        ],
        campaign_id: "987654321",
        creative_id: "456789123",
        form_id: "form_abc123"
      };
    }
    
    const jsonPayload = JSON.stringify(samplePayload);
    
    // Use double quotes and escape them properly for shell
    const escapedJson = jsonPayload.replace(/"/g, '\\"');
    
    return `curl -X POST \\
  "${webhookUrl}" \\
  -H "Content-Type: application/json" \\
  -d "${escapedJson}"`;
  };

  const handleCopyCurl = () => {
    const curlCommand = generateSampleCurl(
      configDialog.webhookUrl, 
      configDialog.integration?.id
    );
    if (curlCommand) {
      navigator.clipboard.writeText(curlCommand);
      openSuccessModal('cURL command copied to clipboard!');
    }
  };

  const getStatusChip = (integration) => {
    if (integration.connected) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Connected"
          color="success"
          size="small"
        />
      );
    }
    return (
      <Chip
        icon={<CancelIcon />}
        label="Not Connected"
        color="default"
        size="small"
      />
    );
  };

  const getTokenStatusInfo = (integration) => {
    if (!integration.connected || !integration.tokenStatus) {
      return null;
    }

    const status = integration.tokenStatus;
    const expiresAt = integration.tokenExpiresAt;
    const daysUntilExpiry = integration.daysUntilExpiry;

    let severity = 'info';
    let statusText = 'Unknown';
    let expiryText = '';

    if (status === 'valid') {
      severity = 'success';
      statusText = 'Active';
      if (expiresAt) {
        const expiryDate = new Date(expiresAt);
        if (daysUntilExpiry !== null && daysUntilExpiry !== undefined) {
          if (daysUntilExpiry < 0) {
            expiryText = `Expired ${Math.abs(daysUntilExpiry)} day(s) ago`;
            severity = 'error';
            statusText = 'Inactive';
          } else if (daysUntilExpiry === 0) {
            expiryText = 'Expires today';
            severity = 'warning';
            statusText = 'Expiring Soon';
          } else if (daysUntilExpiry <= 7) {
            expiryText = `Expires in ${daysUntilExpiry} day(s) - ${expiryDate.toLocaleDateString()}`;
            severity = 'warning';
            statusText = 'Expiring Soon';
          } else {
            expiryText = `Expires on ${expiryDate.toLocaleDateString()} (${daysUntilExpiry} days remaining)`;
          }
        } else {
          expiryText = `Expires on ${expiryDate.toLocaleDateString()}`;
        }
      }
      // If no expiry date, token is active - don't show expiry text
    } else if (status === 'expired') {
      severity = 'error';
      statusText = 'Inactive';
      if (expiresAt) {
        const expiryDate = new Date(expiresAt);
        expiryText = `Expired on ${expiryDate.toLocaleDateString()}`;
      } else {
        expiryText = 'Token has expired';
      }
    } else if (status === 'invalid') {
      severity = 'error';
      statusText = 'Inactive';
      expiryText = 'Token is invalid or revoked';
    } else {
      severity = 'warning';
      statusText = 'Unknown';
      expiryText = 'Unable to verify token status';
    }

    return { severity, statusText, expiryText };
  };

  const IntegrationCard = ({ integration }) => {
    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 4,
          },
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: integration.id === 'facebook' ? '#1877F2' :
                        integration.id === 'whatsapp' ? '#25D366' : 
                        integration.id === 'magicbricks' ? '#ffffff' :
                        integration.id === '99acres' ? '#ffffff' : 
                        integration.id === 'housing' ? '#ffffff' :
                        integration.id === 'google_ads' ? '#ffffff' :
                        'primary.main',
                color: (integration.id === 'housing' || integration.id === 'google_ads') ? '#333' : 'white',
                mr: 2,
                border: (integration.id === 'magicbricks' || integration.id === '99acres' || integration.id === 'housing' || integration.id === 'google_ads') ? '1px solid #e0e0e0' : 'none',
              }}
            >
              {integration.icon}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" component="div">
                {integration.name}
              </Typography>
              {getStatusChip(integration)}
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
            {integration.description}
          </Typography>

          {integration.connected && integration.connectedPage && (
            <Box sx={{ mb: 2 }}>
              {integration.id === 'facebook' && integration.connectedPages && integration.connectedPages.length > 0 ? (
                // Show connected pages count for Facebook (clickable)
                <Alert 
                  severity="success" 
                  icon={<LinkIcon />} 
                  sx={{ 
                    mb: 1,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(46, 125, 50, 0.1)',
                      boxShadow: 1,
                      transform: 'translateY(-1px)'
                    }
                  }}
                  onClick={handleManagePages}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Connected Pages ({integration.connectedPages.length})
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        display="block"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {integration.connectedPages.length <= 2 
                          ? integration.connectedPages.map(p => p.name).join(', ')
                          : `${integration.connectedPages.slice(0, 2).map(p => p.name).join(', ')} and ${integration.connectedPages.length - 2} more`
                        }
                      </Typography>
                      <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block', fontWeight: 500 }}>
                        Click to view all pages →
                      </Typography>
                    </Box>
                    <IconButton 
                      size="small" 
                      sx={{ 
                        ml: 1,
                        color: 'success.main',
                        '&:hover': {
                          bgcolor: 'rgba(46, 125, 50, 0.15)'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleManagePages();
                      }}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Alert>
              ) : (
                // Show single connected account for other integrations
                <Alert severity="success" icon={<LinkIcon />} sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>{integration.id === 'facebook' ? 'Connected Page:' : 'Connected Account:'}</strong> {integration.connectedPage.name}
                  </Typography>
                  {integration.connectedPage.id && integration.id === 'facebook' && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Page ID: {integration.connectedPage.id}
                    </Typography>
                  )}
                  {integration.lastSyncedAt && integration.id !== 'facebook' && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Last synced: {new Date(integration.lastSyncedAt).toLocaleString()}
                    </Typography>
                  )}
                </Alert>
              )}
              
              {/* Token Status */}
              {getTokenStatusInfo(integration) && (() => {
                const tokenInfo = getTokenStatusInfo(integration);
                const handleCheckExpiry = async () => {
                  // Get token from integration details
                  try {
                    const response = await api.get('/lms/integrations/facebook/');
                    const token = response.data.page_access_token;
                    if (token) {
                      const debugUrl = `https://developers.facebook.com/tools/debug/accesstoken/?access_token=${encodeURIComponent(token)}`;
                      window.open(debugUrl, '_blank', 'noopener,noreferrer');
                    } else {
                      openErrorModal('Token not available');
                    }
                  } catch (error) {
                    openErrorModal('Failed to get token for debugging');
                  }
                };
                
                return (
                  <Alert severity={tokenInfo.severity} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2">
                            <strong>Token Status:</strong> {tokenInfo.statusText}
                          </Typography>
                          <Button
                            size="small"
                            variant="text"
                            onClick={handleCheckExpiry}
                            startIcon={<OpenInNewIcon fontSize="small" />}
                            sx={{ 
                              minWidth: 'auto',
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              p: 0.5
                            }}
                          >
                            Check expiry
                          </Button>
                        </Box>
                        {tokenInfo.expiryText && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {tokenInfo.expiryText}
                          </Typography>
                        )}
                      </Box>
                      {tokenInfo.severity === 'error' && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleConnectFacebook}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          Reconnect
                        </Button>
                      )}
                    </Box>
                  </Alert>
                );
              })()}
            </Box>
          )}

          {integration.id === 'facebook' && !integration.connected && (
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
              <Typography variant="caption">
                Click "Connect Facebook Page" to authorize Salesgro to access your Facebook Pages and receive Lead Ads.
              </Typography>
            </Alert>
          )}

          {integration.id === 'facebook' && integration.connected && integration.connectedPages && (
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
              <Typography variant="caption">
                You can connect multiple Facebook pages. Click "Manage Pages" to add more pages or disconnect existing ones.
              </Typography>
            </Alert>
          )}

          {integration.id === 'magicbricks' && !integration.connected && (
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
              <Typography variant="caption">
                Click "Enable Integration" to generate a webhook URL and share it with the MagicBricks backend team.
              </Typography>
            </Alert>
          )}

          {integration.id === '99acres' && !integration.connected && (
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
              <Typography variant="caption">
                Click "Enable Integration" to generate a webhook URL and share it with the 99acres backend team.
              </Typography>
            </Alert>
          )}

          {integration.id === 'housing' && !integration.connected && (
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
              <Typography variant="caption">
                Click "Enable Integration" to generate a webhook URL and share it with the Housing.com backend team.
              </Typography>
            </Alert>
          )}

          {integration.id === 'google_ads' && !integration.connected && (
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
              <Typography variant="caption">
                Click "Enable Integration" to generate a webhook URL and configure it in Google Ads Lead Form Extensions.
              </Typography>
            </Alert>
          )}

          {integration.id === 'whatsapp' && !integration.connected && (
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
              <Typography variant="caption">
                Click "Connect WhatsApp" to authorize Salesgro to send and receive WhatsApp messages via your WhatsApp Business Account.
              </Typography>
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            {integration.connected ? (
              <>
                <Tooltip title="Refresh connection status">
                  <IconButton
                    size="small"
                    onClick={() => fetchIntegrations()}
                    disabled={loading}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                {(integration.id === 'magicbricks' || integration.id === '99acres' || integration.id === 'housing' || integration.id === 'google_ads') && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      try {
                        showLoader('Fetching webhook URL...');
                        let endpoint = '';
                        if (integration.id === 'magicbricks') {
                          endpoint = '/lms/integrations/magicbricks/setup/';
                        } else if (integration.id === '99acres') {
                          endpoint = '/lms/integrations/99acres/setup/';
                        } else if (integration.id === 'housing') {
                          endpoint = '/lms/integrations/housing/setup/';
                        } else if (integration.id === 'google_ads') {
                          endpoint = '/lms/integrations/google-ads/setup/';
                        }
                        const response = await api.get(endpoint);
                        setConfigDialog({
                          open: true,
                          integration,
                          apiKey: '',
                          webhookUrl: response.data.webhook_url
                        });
                      } catch (error) {
                        openErrorModal('Failed to fetch webhook URL');
                      } finally {
                        hideLoader();
                      }
                    }}
                  >
                    View Webhook URL
                  </Button>
                )}
                {integration.id === 'facebook' ? (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={handleManagePages}
                    startIcon={<FacebookIcon />}
                  >
                    Manage Pages
                  </Button>
                ) : integration.id === 'whatsapp' ? (
                  <>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => navigate('/whatsapp')}
                      startIcon={<WhatsAppIcon />}
                    >
                      Open WhatsApp
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => setDisconnectDialog({ open: true, integration })}
                    >
                      Disconnect
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => setDisconnectDialog({ open: true, integration })}
                  >
                    Disconnect
                  </Button>
                )}
              </>
            ) : (
              <>
                {integration.id === 'whatsapp' && (
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={handleConnectWhatsApp}
                    disabled={loading}
                    startIcon={<WhatsAppIcon />}
                  >
                    Connect WhatsApp
                  </Button>
                )}
                {integration.id === 'facebook' && (
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleConnectFacebook}
                    disabled={loading}
                    startIcon={<LinkIcon />}
                  >
                    Connect Facebook Page
                  </Button>
                )}
                {(integration.id === 'magicbricks' || integration.id === '99acres' || integration.id === 'housing' || integration.id === 'google_ads') && (
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => {
                      setConfigDialog({ 
                        open: true, 
                        integration, 
                        apiKey: '',
                        webhookUrl: '' 
                      });
                      // Immediately generate webhook URL
                      if (integration.id === 'magicbricks') {
                        handleConfigureMagicBricks();
                      } else if (integration.id === '99acres') {
                        handleConfigure99Acres();
                      } else if (integration.id === 'housing') {
                        handleConfigureHousing();
                      } else if (integration.id === 'google_ads') {
                        handleConfigureGoogleAds();
                      }
                    }}
                    disabled={loading}
                    startIcon={<LinkIcon />}
                  >
                    Enable Integration
                  </Button>
                )}
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Integrations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Connect your external platforms to automatically receive leads and sync data with Salesgro.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Custom Webhooks Card */}
        <Grid item xs={12} md={6} lg={4}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px)',
              },
            }}
            onClick={() => navigate('/settings/custom-webhooks')}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'primary.main',
                    color: 'white',
                    mr: 2,
                  }}
                >
                  <WebhookIcon />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="div">
                    Custom Webhooks
                  </Typography>
                  <Chip
                    label={customWebhooksCount > 0 ? `${customWebhooksCount} Active` : 'Not Configured'}
                    color={customWebhooksCount > 0 ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                Create custom webhooks with field mapping to receive leads from any external source
              </Typography>

              {customWebhooksCount > 0 && (
                <Alert severity="success" icon={<LinkIcon />} sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>{customWebhooksCount} webhook{customWebhooksCount !== 1 ? 's' : ''} configured</strong>
                  </Typography>
                </Alert>
              )}

              <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
                <Typography variant="caption">
                  Configure field mappings to automatically convert incoming webhook data into Salesgro leads.
                </Typography>
              </Alert>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<SettingsIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/settings/custom-webhooks');
                  }}
                >
                  Manage Webhooks
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Existing Integrations */}
        {integrations.map((integration) => (
          <Grid item xs={12} md={6} lg={4} key={integration.id}>
            <IntegrationCard integration={integration} />
          </Grid>
        ))}
      </Grid>

      {integrations.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No integrations available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check back later for new integration options.
          </Typography>
        </Box>
      )}

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={disconnectDialog.open}
        onClose={() => setDisconnectDialog({ open: false, integration: null })}
      >
        <DialogTitle>Disconnect {disconnectDialog.integration?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to disconnect {disconnectDialog.integration?.name}? 
            You will no longer receive leads from this integration. This action can be undone by reconnecting.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialog({ open: false, integration: null })}>
            Cancel
          </Button>
          <Button onClick={handleDisconnect} color="error" variant="contained">
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>

      {/* Configuration Dialog for MagicBricks and 99acres */}
      <Dialog 
        open={configDialog.open} 
        onClose={() => setConfigDialog({ open: false, integration: null, apiKey: '', webhookUrl: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Enable {configDialog.integration?.name} Integration</DialogTitle>
        <DialogContent>
          {configDialog.webhookUrl ? (
            <>
              <Alert severity="success" sx={{ mb: 3, mt: 1 }}>
                Integration enabled successfully!
              </Alert>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Your Webhook URL
              </Typography>
              <DialogContentText sx={{ mb: 2 }}>
                Share this webhook URL with the {configDialog.integration?.name} backend team. 
                They will configure it on their end to send leads directly to your Salesgro account.
              </DialogContentText>
              
              <TextField
                fullWidth
                label="Webhook URL"
                value={configDialog.webhookUrl}
                InputProps={{
                  readOnly: true,
                }}
                sx={{ mb: 2 }}
                multiline
                rows={2}
              />
              
              <Button
                variant="outlined"
                fullWidth
                onClick={handleCopyWebhookUrl}
                startIcon={<LinkIcon />}
                sx={{ mb: 3 }}
              >
                Copy Webhook URL
              </Button>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Sample cURL Command
              </Typography>
              <DialogContentText sx={{ mb: 2 }}>
                Use this sample cURL command to test the webhook integration. You can modify the payload as needed.
              </DialogContentText>
              
              <Box
                sx={{
                  position: 'relative',
                  bgcolor: '#f5f5f5',
                  borderRadius: 1,
                  p: 2,
                  mb: 2,
                  border: '1px solid #e0e0e0',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    Terminal Command
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleCopyCurl}
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    margin: 0,
                    color: '#333',
                    overflowX: 'auto',
                  }}
                >
                  {generateSampleCurl(configDialog.webhookUrl, configDialog.integration?.id)}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                Next Steps
              </Typography>
              <Alert severity="info">
                <Typography variant="body2" component="div">
                  <ol style={{ margin: 0, paddingLeft: '20px' }}>
                    <li>Copy the webhook URL above</li>
                    <li>Contact {configDialog.integration?.name} support team</li>
                    <li>Share the webhook URL with them for configuration</li>
                    <li>Once configured, leads will automatically sync to Salesgro</li>
                  </ol>
                </Typography>
              </Alert>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Generating webhook URL...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfigDialog({ open: false, integration: null, apiKey: '', webhookUrl: '' })}
            variant="contained"
          >
            {configDialog.webhookUrl ? 'Done' : 'Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp Wizard Dialog */}
      <Dialog
        open={whatsappWizard.open}
        onClose={() => {
          setWhatsappWizard({ open: false, step: 1, wabas: [], phoneNumbers: [], selectedWaba: null, selectedPhone: null });
          setWhatsappWizardLoading(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Connect WhatsApp
          <Stepper activeStep={whatsappWizard.step - 1} sx={{ mt: 2, mb: 2 }}>
            <Step><StepLabel>Authorize</StepLabel></Step>
            <Step><StepLabel>Select Account</StepLabel></Step>
            <Step><StepLabel>Select Phone</StepLabel></Step>
          </Stepper>
        </DialogTitle>
        <DialogContent>
          {whatsappWizardLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(255, 255, 255, 0.8)', zIndex: 1 }}>
              <CircularProgress />
            </Box>
          )}
          {whatsappWizard.step === 1 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Waiting for authorization...
              </Typography>
            </Box>
          )}
          
          {whatsappWizard.step === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Select WhatsApp Business Account
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose which WhatsApp Business Account you want to connect:
              </Typography>
              {whatsappWizard.wabas.length === 0 ? (
                <Alert severity="warning">
                  No WhatsApp Business Accounts found. Please ensure you have a WABA set up in Meta Business Manager.
                </Alert>
              ) : (
                <List>
                  {whatsappWizard.wabas.map((waba) => (
                    <ListItem
                      key={waba.id}
                      disablePadding
                      sx={{ mb: 1 }}
                    >
                      <ListItemButton
                        onClick={() => fetchPhoneNumbers(waba.id)}
                        selected={whatsappWizard.selectedWaba?.id === waba.id}
                        disabled={whatsappWizardLoading}
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          '&.Mui-selected': {
                            borderColor: '#25D366',
                            bgcolor: '#f0f9f4',
                          },
                        }}
                      >
                        <ListItemText
                          primary={waba.name || 'Unnamed WABA'}
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block">
                                Business: {waba.business_name}
                              </Typography>
                              <Typography variant="caption" display="block" color={waba.is_enabled ? 'success.main' : 'error.main'}>
                                Status: {waba.is_enabled ? 'Enabled' : 'Disabled'}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemIcon sx={{ minWidth: 'auto', ml: 2 }}>
                          <CheckCircleIcon 
                            color={whatsappWizard.selectedWaba?.id === waba.id ? 'success' : 'disabled'} 
                          />
                        </ListItemIcon>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
          
          {whatsappWizard.step === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Select Phone Number
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose which phone number to use for WhatsApp messaging:
              </Typography>
              {whatsappWizard.phoneNumbers.length === 0 ? (
                <Alert severity="warning">
                  No phone numbers found for this WhatsApp Business Account.
                </Alert>
              ) : (
                <List>
                  {whatsappWizard.phoneNumbers.map((phone) => (
                    <ListItem
                      key={phone.id}
                      disablePadding
                      sx={{ mb: 1 }}
                    >
                      <ListItemButton
                        onClick={() => setWhatsappWizard(prev => ({ ...prev, selectedPhone: phone }))}
                        selected={whatsappWizard.selectedPhone?.id === phone.id}
                        disabled={whatsappWizardLoading}
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          '&.Mui-selected': {
                            borderColor: phone.verification_status === 'EXPIRED' ? '#f44336' : '#25D366',
                            bgcolor: phone.verification_status === 'EXPIRED' ? '#fff5f5' : '#f0f9f4',
                          },
                          ...(phone.verification_status === 'EXPIRED' && {
                            borderColor: whatsappWizard.selectedPhone?.id === phone.id ? '#f44336' : '#f44336',
                            bgcolor: whatsappWizard.selectedPhone?.id === phone.id ? '#fff5f5' : '#fff5f5',
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: '#fff5f5',
                              borderColor: '#f44336',
                            },
                          }),
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{phone.phone_number || 'Unknown'}</span>
                              {phone.verification_status === 'EXPIRED' && (
                                <Chip 
                                  label="EXPIRED" 
                                  color="error" 
                                  size="small" 
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block">
                                Verified Name: {phone.verified_name || 'N/A'}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                display="block"
                                sx={{
                                  color: phone.verification_status === 'EXPIRED' ? 'error.main' : 'text.secondary'
                                }}
                              >
                                Quality: {phone.quality_rating} | Status: {phone.verification_status}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemIcon sx={{ minWidth: 'auto', ml: 2 }}>
                          <CheckCircleIcon 
                            color={whatsappWizard.selectedPhone?.id === phone.id ? 'success' : 'disabled'} 
                          />
                        </ListItemIcon>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
              {whatsappWizard.selectedPhone && (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSaveWhatsApp}
                  disabled={whatsappWizardLoading}
                  sx={{ mt: 2, bgcolor: '#25D366', '&:hover': { bgcolor: '#20BA5A' } }}
                >
                  {whatsappWizardLoading ? 'Connecting...' : 'Connect WhatsApp'}
                </Button>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setWhatsappWizard({ open: false, step: 1, wabas: [], phoneNumbers: [], selectedWaba: null, selectedPhone: null });
              setWhatsappWizardLoading(false);
            }}
            disabled={whatsappWizardLoading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Facebook Page Selection Dialog */}
      <Dialog
        open={pageSelectionDialog.open}
        onClose={() => setPageSelectionDialog({ open: false, pages: [], loading: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FacebookIcon color="primary" />
            <Typography variant="h6">Select Facebook Pages</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {pageSelectionDialog.loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Loading your Facebook pages...</Typography>
            </Box>
          ) : pageSelectionDialog.pages.length > 0 ? (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Select which Facebook page(s) you want to connect to Salesgro. You can connect multiple pages.
              </DialogContentText>
              
              {pageSelectionDialog.pages.map((page) => (
                <Card 
                  key={page.id} 
                  sx={{ 
                    mb: 2,
                    border: page.is_connected ? '2px solid' : '1px solid',
                    borderColor: page.is_connected ? 'success.main' : 'divider',
                    bgcolor: page.is_connected ? 'success.50' : 'background.paper'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {page.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Page ID: {page.id}
                        </Typography>
                        {page.is_connected && (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Connected"
                            color="success"
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                      <Box>
                        {page.is_connected ? (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleDisconnectPage(page.id)}
                          >
                            Disconnect
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => handleConnectPage(page.id)}
                          >
                            Connect
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>Note:</strong> Each connected page will receive Lead Ads directly in Salesgro.
                  You can manage or disconnect pages at any time.
                </Typography>
              </Alert>
            </>
          ) : (
            <Alert severity="warning">
              <Typography variant="body2">
                No Facebook pages found. Please make sure you have Facebook pages and have granted the necessary permissions.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setPageSelectionDialog({ open: false, pages: [], loading: false })}
            variant="outlined"
          >
            Close
          </Button>
          <Button
            onClick={handleConnectFacebook}
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
          >
            Reconnect OAuth
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Integrations;

